import React from 'react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const GrantDetailModal = ({ grant, onClose }) => {
  const getStatusColor = (status) => {
    const colorMap = {
      open: 'bg-green-100 text-green-800',
      closing_soon: 'bg-yellow-100 text-yellow-800',
      closed: 'bg-red-100 text-red-800',
      review: 'bg-blue-100 text-blue-800',
      awarded: 'bg-purple-100 text-purple-800'
    };
    return colorMap?.[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      open: 'CircleDot',
      closing_soon: 'Clock',
      closed: 'XCircle',
      review: 'Eye',
      awarded: 'Award'
    };
    return iconMap?.[status] || 'CircleDot';
  };

  const formatAmount = (min, max, currency = 'USD') => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    if (min && max && min !== max) {
      return `${formatter?.format(min)} - ${formatter?.format(max)}`;
    }
    if (max) {
      return `Up to ${formatter?.format(max)}`;
    }
    if (min) {
      return `From ${formatter?.format(min)}`;
    }
    return 'Amount TBD';
  };

  const handleApply = () => {
    if (grant?.application_url) {
      window.open(grant?.application_url, '_blank');
    }
  };

  const handleBookmark = () => {
    // In a real app, this would save to user's bookmarks console.log('Bookmarked grant:', grant?.id);
  };

  const handleShare = async () => {
    const shareData = {
      title: grant?.title,
      text: `Check out this grant opportunity: ${grant?.title} from ${grant?.provider_organization}`,
      url: window.location?.href
    };

    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard?.writeText(`${shareData?.text} ${shareData?.url}`);
        // Show toast notification here
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Grant Opportunity Details</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center space-x-2 mb-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(grant?.status)}`}>
                    <Icon name={getStatusIcon(grant?.status)} size={14} className="mr-2" />
                    {grant?.status?.charAt(0)?.toUpperCase() + grant?.status?.slice(1)?.replace('_', ' ')}
                  </span>
                </div>

                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {grant?.title}
                </h1>

                <p className="text-muted-foreground mb-4">{grant?.provider_organization}</p>

                <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="DollarSign" size={16} />
                    <span className="font-medium">
                      {formatAmount(grant?.amount_min, grant?.amount_max, grant?.currency)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Calendar" size={16} />
                    <span>Due {format(new Date(grant?.deadline), 'MMMM dd, yyyy')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Eye" size={16} />
                    <span>{grant?.view_count || 0} views</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Description</h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-muted-foreground leading-relaxed">
                    {grant?.description || 'No description available for this grant opportunity.'}
                  </p>
                </div>
              </div>

              {/* Focus Areas */}
              {grant?.focus_areas && grant?.focus_areas?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Focus Areas</h3>
                  <div className="flex flex-wrap gap-2">
                    {grant?.focus_areas?.map((area, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-primary/10 text-primary"
                      >
                        {area}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Eligibility Criteria */}
              {grant?.eligibility_criteria && grant?.eligibility_criteria?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Eligibility Criteria</h3>
                  <ul className="space-y-2">
                    {grant?.eligibility_criteria?.map((criteria, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon name="Check" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground text-sm">{criteria}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Requirements */}
              {grant?.requirements && grant?.requirements?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Application Requirements</h3>
                  <ul className="space-y-2">
                    {grant?.requirements?.map((requirement, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon name="FileText" size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground text-sm">{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Documents */}
              {grant?.document_urls && grant?.document_urls?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Related Documents</h3>
                  <div className="space-y-2">
                    {grant?.document_urls?.map((doc, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(doc, '_blank')}
                        className="mr-2 mb-2"
                      >
                        <Icon name="Download" size={14} className="mr-2" />
                        Document {index + 1}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Application Actions */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="font-semibold mb-3">Take Action</h3>
                <div className="space-y-2">
                  {grant?.application_url ? (
                    <Button onClick={handleApply} className="w-full">
                      <Icon name="ExternalLink" size={16} className="mr-2" />
                      Apply Now
                    </Button>
                  ) : (
                    <Button disabled className="w-full">
                      <Icon name="Lock" size={16} className="mr-2" />
                      Application Link TBD
                    </Button>
                  )}
                  
                  <Button variant="outline" onClick={handleBookmark} className="w-full">
                    <Icon name="Bookmark" size={16} className="mr-2" />
                    Bookmark
                  </Button>
                  
                  <Button variant="outline" onClick={handleShare} className="w-full">
                    <Icon name="Share2" size={16} className="mr-2" />
                    Share Grant
                  </Button>
                </div>
              </div>

              {/* Grant Information */}
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="font-semibold mb-3">Grant Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Award Amount</p>
                    <p className="text-sm">
                      {formatAmount(grant?.amount_min, grant?.amount_max, grant?.currency)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Application Deadline</p>
                    <p className="text-sm">{format(new Date(grant?.deadline), 'MMMM dd, yyyy')}</p>
                  </div>

                  {grant?.geographic_scope && grant?.geographic_scope?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Geographic Scope</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {grant?.geographic_scope?.map((scope, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground"
                          >
                            {scope}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Posted By</p>
                    <p className="text-sm">
                      {grant?.creator?.full_name}
                      {grant?.creator?.organization && (
                        <span className="text-muted-foreground"> • {grant?.creator?.organization}</span>
                      )}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Posted On</p>
                    <p className="text-sm">{format(new Date(grant?.created_at), 'MMMM dd, yyyy')}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              {grant?.creator?.email && (
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">Contact Information</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `mailto:${grant?.creator?.email}?subject=Inquiry about ${grant?.title}`}
                    className="w-full"
                  >
                    <Icon name="Mail" size={14} className="mr-2" />
                    Contact Grant Administrator
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrantDetailModal;